import { prettySaltCategory } from '../utils/SaltCategoryLabels';
import { preferServerCopy } from './profileCopy';

const pct = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return `${Math.round(n * 100)}%`;
};

const pct1 = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return `${(n * 100).toFixed(1)}%`;
};

const num = (v, digits = 1) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return n.toFixed(digits);
};

const cat = (key) => prettySaltCategory(key) || key;

const RANK_WORD = { 2: 'second', 3: 'third' };

const DRIVERS = {
    categoryLead: (d) => ({
        tone: 'alert',
        title: `${cat(d.category)} leads the salt`,
        body: `${pct(d.share)} of deck salt (${d.pieceCount} ${d.pieceCount === 1 ? 'piece' : 'pieces'})${
            d.topCardName ? ` — top card ${d.topCardName}` : ''
        }.`,
    }),
    categorySupport: (d) => ({
        tone: 'warn',
        title: `${cat(d.category)} is the ${RANK_WORD[d.rank] || 'next'} heaviest`,
        body: `${pct(d.share)} of deck salt (${d.pieceCount} ${d.pieceCount === 1 ? 'piece' : 'pieces'})${
            d.topCardName ? ` — ${d.topCardName} leads this bucket` : ''
        }.`,
    }),
    staxStack: (d) => ({
        tone: 'alert',
        title: 'Stax stack',
        body: `${d.count} stax sub-categories stacked (${(d.categories || [])
            .map(cat)
            .join(', ')}) — combined ${pct(d.combinedShare)} of deck salt.`,
    }),
    landDenialPackage: (d) => ({
        tone: 'alert',
        title: 'Land-denial package',
        body: `Mass land denial (${pct(d.mldShare)}) riding on off-color fetches (${pct(
            d.offColorFetchShare
        )}) — pods will feel the lockout.`,
    }),
    comboPlusLockout: (d) => ({
        tone: 'alert',
        title: 'Combo + lockout',
        body: `Infinite combos (${pct(d.comboShare)}) backed by lock pieces (${pct(
            d.lockShare
        )}) — stop the table, then win.`,
    }),
    comboPlusMetaDensity: (d) => ({
        tone: 'alert',
        title: 'Combo + cEDH staples',
        body: `${d.comboPieces || 0} combo pieces (${pct(d.comboShare)}) woven into ${d.metaPieces || 0} meta staples (${pct(
            d.metaShare
        )}) — the classic spike signature.`,
    }),
    tunedShellSignal: (d) => ({
        tone: 'alert',
        title: 'Tuned shell signal',
        body: `Power level ${num(d.powerLevel, 1)} with ${d.metaPieces || 0} meta staples (${pct(
            d.metaShare
        )}) — this shell is optimized hard.`,
    }),
    handHateStack: (d) => ({
        tone: 'warn',
        title: 'Hand-hate stack',
        body: `Wheels (${pct(d.wheelsShare)}) and targeted discard (${pct(
            d.discardShare
        )}) team up to empty grips.`,
    }),
    punisherStack: (d) => ({
        tone: 'warn',
        title: 'Punisher stack',
        body: `Group-slug pings (${pct(d.groupSlugShare)}) on top of life-gain shutoff (${pct(
            d.lifeLockShare
        )}) — bleed them without letting them heal.`,
    }),
    combatLoop: (d) => ({
        tone: 'warn',
        title: 'Combat loop',
        body: `Extra turns (${pct(d.extraTurnsShare)}) plus extra combats (${pct(
            d.extraCombatsShare
        )}) — your attack step just keeps going.`,
    }),
    walletStack: (d) => ({
        tone: 'warn',
        title: 'Wallet stack',
        body: `High-dollar cards (${pct(d.priceShare)}) on top of a reserved-list presence (${pct(
            d.reservedShare
        )}) — the price tag speaks.`,
    }),
    theftHeavy: (d) => ({
        tone: 'warn',
        title: 'Theft-heavy',
        body: `Theft is ${pct(d.share)} of the salt — ${d.pieceCount || 0} ${
            (d.pieceCount || 0) === 1 ? 'piece' : 'pieces'
        }${d.topCardName ? ` led by ${d.topCardName}` : ''}.`,
    }),
    priceDominated: (d) => ({
        tone: 'alert',
        title: 'Price dominates',
        body: `Card price alone is ${pct(d.share)} of total salt across ${
            d.pieceCount || 0
        } cards.`,
    }),
    reservedListPresence: (d) => ({
        tone: 'warn',
        title: 'Reserved-list presence',
        body: `${d.pieceCount || 0} reserved-list cards make up ${pct(d.share)} of deck salt.`,
    }),
    chaosPresence: (d) => ({
        tone: 'warn',
        title: 'Chaos presence',
        body: `Chaos effects contribute ${pct(d.share)} across ${d.pieceCount || 0} ${
            (d.pieceCount || 0) === 1 ? 'card' : 'cards'
        }.`,
    }),
    annihilatorPresence: (d) => ({
        tone: 'alert',
        title: 'Annihilator presence',
        body: `${d.pieceCount || 0} annihilator ${
            (d.pieceCount || 0) === 1 ? 'source' : 'sources'
        } (${pct(d.share)}) — attacks eat permanents.`,
    }),
    poisonClock: (d) => ({
        tone: 'alert',
        title: 'Poison clock',
        body: `${d.pieceCount || 0} poison ${
            (d.pieceCount || 0) === 1 ? 'piece' : 'pieces'
        } (${pct(d.share)}) — the 10-counter countdown is on.`,
    }),
    sacrificeEngine: (d) => ({
        tone: 'warn',
        title: 'Sacrifice engine',
        body: `${d.pieceCount || 0} forced-sacrifice effects (${pct(
            d.share
        )}) push the pod into bad trades.`,
    }),
};

const MITIGATION = {
    trimPricePieces: (d) => ({
        title: 'Trim price pieces',
        body: `${d.pieceCount || 0} cards carry ${pct(d.share)} of salt on price alone${
            d.topCardName ? `; swapping ${d.topCardName} drops ~${num(d.topCardScore, 1)}` : ''
        }${d.estimatedCut ? ` (~${num(d.estimatedCut, 1)} salt off the top)` : ''}.`,
    }),
    dropReservedStaples: (d) => ({
        title: 'Drop reserved-list staples',
        body: `Reserved-list picks add ${pct(d.share)} of salt across ${d.pieceCount || 0} ${
            (d.pieceCount || 0) === 1 ? 'card' : 'cards'
        }${d.topCardName ? ` — e.g. ${d.topCardName}` : ''}.`,
    }),
    cutExtraTurns: (d) => ({
        title: 'Cut extra-turn effects',
        body: `${d.pieceCount || 0} extra-turn ${
            (d.pieceCount || 0) === 1 ? 'spell' : 'spells'
        } make up ${pct(d.share)} of deck salt.`,
    }),
    cutMassLandDenial: (d) => ({
        title: 'Cut mass land denial',
        body: `${d.pieceCount || 0} MLD ${
            (d.pieceCount || 0) === 1 ? 'card' : 'cards'
        } contribute ${pct(d.share)} of deck salt.`,
    }),
    cutTheftPieces: (d) => ({
        title: 'Cut theft pieces',
        body: `Theft effects add ${pct(d.share)} across ${d.pieceCount || 0} ${
            (d.pieceCount || 0) === 1 ? 'card' : 'cards'
        }${d.topCardName ? ` — starting with ${d.topCardName}` : ''}.`,
    }),
    loosenStax: (d) => ({
        title: 'Loosen the stax pieces',
        body: `Stax sub-categories (${(d.categories || [])
            .map(cat)
            .join(', ')}) are ${pct(d.combinedShare)} combined — loosen a few.`,
    }),
    thinLocks: (d) => ({
        title: 'Thin the lock pieces',
        body: `Hard locks account for ${pct(d.share)} across ${d.pieceCount || 0} ${
            (d.pieceCount || 0) === 1 ? 'line' : 'lines'
        }.`,
    }),
    replaceTopCard: (d) => ({
        title: `Replace ${d.cardName || 'the top card'}`,
        body: `Contributes ${num(d.cardScore, 1)} salt (~${pct(d.contribution)} of the deck total)${
            d.category ? ` in ${cat(d.category)}` : ''
        }.`,
    }),
    replaceTopCombo: (d) => ({
        title: `Replace ${d.comboName || 'the top combo'}`,
        body: `Single combo line contributes ${num(d.comboScore, 1)} salt (~${pct(
            d.contribution
        )} of deck total).`,
    }),
    softenGroupSlug: (d) => ({
        title: 'Soften group slug',
        body: `Group-slug pings add ${pct(d.share)} across ${d.pieceCount || 0} ${
            (d.pieceCount || 0) === 1 ? 'source' : 'sources'
        }.`,
    }),
    softenHandHate: (d) => ({
        title: 'Soften hand hate',
        body: `Wheels (${pct(d.wheelsShare)}) and discard (${pct(d.discardShare)}) stack — pull back on one.`,
    }),
    diversifySalt: (d) => ({
        title: 'Diversify the salt',
        body: `${cat(d.dominantCategory)} is ${pct(
            d.dominantShare
        )} of deck salt on its own — spreading it out reads less targeted.`,
    }),
};

const POD_DISCLOSE = {
    massLandDenialPresent: (d) => ({
        title: 'Mass land denial',
        body: `${d.pieceCount || 0} MLD ${
            (d.pieceCount || 0) === 1 ? 'piece' : 'pieces'
        } (${pct(d.share)}).`,
    }),
    extraTurnsPresent: (d) => ({
        title: 'Extra turns',
        body: `${d.pieceCount || 0} extra-turn ${
            (d.pieceCount || 0) === 1 ? 'spell' : 'spells'
        } (${pct(d.share)}).`,
    }),
    extraCombatsPresent: (d) => ({
        title: 'Extra combats',
        body: `${d.pieceCount || 0} extra-combat ${
            (d.pieceCount || 0) === 1 ? 'spell' : 'spells'
        } (${pct(d.share)}).`,
    }),
    infiniteCombosPresent: (d) => ({
        title: 'Infinite combos',
        body: `${d.pieceCount || 0} combo ${
            (d.pieceCount || 0) === 1 ? 'line' : 'lines'
        } (${pct(d.share)})${d.topComboName ? ` — e.g. ${d.topComboName}` : ''}.`,
    }),
    locksPresent: (d) => ({
        title: 'Hard locks',
        body: `${d.pieceCount || 0} lock ${
            (d.pieceCount || 0) === 1 ? 'piece' : 'pieces'
        } (${pct(d.share)}).`,
    }),
    theftPresent: (d) => ({
        title: 'Theft',
        body: `${d.pieceCount || 0} theft ${
            (d.pieceCount || 0) === 1 ? 'piece' : 'pieces'
        } (${pct(d.share)})${d.topCardName ? ` — e.g. ${d.topCardName}` : ''}.`,
    }),
    chaosPresent: (d) => ({
        title: 'Chaos',
        body: `${d.pieceCount || 0} chaos ${
            (d.pieceCount || 0) === 1 ? 'effect' : 'effects'
        } (${pct(d.share)}).`,
    }),
    staxLockoutPresent: (d) => ({
        title: 'Stax / lockout pieces',
        body: `${(d.categories || []).map(cat).join(', ')} — combined ${pct(d.combinedShare)}.`,
    }),
    lifeGainShutOff: (d) => ({
        title: 'Life-gain shutoff',
        body: `${d.pieceCount || 0} ${
            (d.pieceCount || 0) === 1 ? 'piece' : 'pieces'
        } (${pct(d.share)}) turn off healing.`,
    }),
    poisonClockPresent: (d) => ({
        title: 'Poison',
        body: `${d.pieceCount || 0} poison ${
            (d.pieceCount || 0) === 1 ? 'source' : 'sources'
        } (${pct(d.share)}).`,
    }),
    annihilatorPresent: (d) => ({
        title: 'Annihilator',
        body: `${d.pieceCount || 0} annihilator ${
            (d.pieceCount || 0) === 1 ? 'source' : 'sources'
        } (${pct(d.share)}).`,
    }),
    groupSlugPresent: (d) => ({
        title: 'Group slug',
        body: `${d.pieceCount || 0} group-slug ${
            (d.pieceCount || 0) === 1 ? 'source' : 'sources'
        } (${pct(d.share)}).`,
    }),
    sacrificeForced: (d) => ({
        title: 'Forced sacrifice',
        body: `${d.pieceCount || 0} forced-sacrifice ${
            (d.pieceCount || 0) === 1 ? 'effect' : 'effects'
        } (${pct(d.share)}).`,
    }),
    payTheOnePresent: (d) => ({
        title: 'Pay-the-one effects',
        body: `${d.pieceCount || 0} “pay the one” ${
            (d.pieceCount || 0) === 1 ? 'piece' : 'pieces'
        } (${pct(d.share)}).`,
    }),
};

const AMPLIFIERS = {
    multiFamilyStack: (d) => ({
        title: 'Multi-family stack',
        body: `${d.familyCount || (d.families || []).length || 0} salt families stacked: ${(
            d.families || []
        ).join(', ')}.`,
    }),
    staxWithCombo: (d) => ({
        title: 'Stax + combo',
        body: `Stax (${pct(d.staxShare)}) paired with infinite combos (${pct(
            d.comboShare
        )}) — stop the table, then close.`,
    }),
    landDenialWithStax: (d) => ({
        title: 'Land denial + stax',
        body: `MLD (${pct(d.mldShare)}) + stax (${pct(d.staxShare)}) — nothing untaps, nothing resolves.`,
    }),
    landDenialWithCombo: (d) => ({
        title: 'Land denial + combo',
        body: `MLD (${pct(d.mldShare)}) sweeping for a combo finish (${pct(d.comboShare)}).`,
    }),
    theftWithCombo: (d) => ({
        title: 'Theft + combo',
        body: `Theft (${pct(d.theftShare)}) alongside combos (${pct(
            d.comboShare
        )}) — take the piece, win with it.`,
    }),
    groupSlugWithLifeLock: (d) => ({
        title: 'Group slug + life-gain lock',
        body: `Pings (${pct(d.groupSlugShare)}) with healing shut off (${pct(
            d.lifeLockShare
        )}) — bleed them out.`,
    }),
    extraTurnLoop: (d) => ({
        title: 'Extra-turn loop',
        body: `Extra turns (${pct(d.extraTurnsShare)}) stapled to combo (${pct(
            d.comboShare
        )}) — one loop closes it.`,
    }),
};

function resolve(map, entry) {
    if (!entry || typeof entry.id !== 'string') return null;
    const fn = map[entry.id];
    let copy = null;
    if (fn) {
        try {
            copy = fn(entry.data || {});
        } catch (err) {
            copy = null;
        }
    }
    // Server copy wins (title←label, body←why); the client copy map is the
    // fallback for old cached decks, and unmapped ids still show via server label.
    const title = preferServerCopy(entry.label, copy?.title);
    const body = preferServerCopy(entry.why, copy?.body);
    if (!title && !body) return null;
    return { id: entry.id, ...(copy || {}), title, body };
}

const HIDDEN_DRIVER_CATEGORIES = new Set(['edhrec']);

export function resolveDrivers(list) {
    return stripHiddenCategoryDrivers(list || [])
        .map((e) => resolve(DRIVERS, e))
        .filter(Boolean);
}

// Hide drivers for categories we don't want to surface (e.g. edhrec), then
// renumber so the remaining category entries still read as "leads / second
// heaviest / third heaviest" in order. Without this, hiding rank 1 would
// leave "X is the second heaviest" as the first visible line — a tell that
// something was suppressed.
function stripHiddenCategoryDrivers(list) {
    const kept = list.filter((e) => !HIDDEN_DRIVER_CATEGORIES.has(e?.data?.category));
    let leadAssigned = false;
    let nextRank = 2;
    return kept.map((e) => {
        if (e?.id === 'categoryLead') {
            leadAssigned = true;
            return e;
        }
        if (e?.id === 'categorySupport') {
            if (!leadAssigned) {
                leadAssigned = true;
                return { ...e, id: 'categoryLead', data: { ...e.data } };
            }
            const rank = nextRank++;
            return { ...e, data: { ...e.data, rank } };
        }
        return e;
    });
}

export function resolveMitigation(list) {
    return (list || []).map((e) => resolve(MITIGATION, e)).filter(Boolean);
}

export function resolvePodDisclose(list) {
    return (list || []).map((e) => resolve(POD_DISCLOSE, e)).filter(Boolean);
}

export function resolveAmplifiers(list) {
    return (list || []).map((e) => resolve(AMPLIFIERS, e)).filter(Boolean);
}

export const SALT_CONCENTRATION_COPY = {
    empty: { label: 'No salt detected', body: 'Nothing in here reads as salty.' },
    dominated: {
        label: 'Dominated salt',
        body: 'One category carries most of the load — everything else is background noise.',
    },
    focused: {
        label: 'Focused salt',
        body: 'Two categories together carry the bulk of the salt.',
    },
    spread: {
        label: 'Spread salt',
        body: 'Top three categories share the load — the salt is broad but real.',
    },
    diffuse: {
        label: 'Diffuse salt',
        body: 'Salt is smeared across many categories — no single thing to cut.',
    },
};

export const SALT_DENSITY_TIERS = {
    trace: { label: 'Trace', cssKey: 'trace' },
    mild: { label: 'Mild', cssKey: 'mild' },
    moderate: { label: 'Moderate', cssKey: 'moderate' },
    heavy: { label: 'Heavy', cssKey: 'heavy' },
    extreme: { label: 'Extreme', cssKey: 'extreme' },
    none: { label: 'None', cssKey: 'trace' },
};

export function densityTier(density) {
    return SALT_DENSITY_TIERS[density] || SALT_DENSITY_TIERS.mild;
}
